import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { QES_SUPERSEDED_LOOPBLOCK } from '~~/common/config/QueueElementState';
import { uuid4 } from '~~/common/utils';
import { isApiError } from '~~/common/utils/apiError';

const LOADING_IDS_BY_ACTION_NAME: Record<string, string> = {
  'order/addToCartRequest': 'add-to-cart-{data.objectId}-{data.stockType}',
  'order/fetchOrderRequest': 'fetch-order-{id}',
  'order/addCouponRequest': 'add-to-coupon-{objectId}',
  'order/updateCartItemRequest': 'update-cart-item-{uuid}',
  'order/removeCartItemRequest': 'remove-cart-item-{uuid}',
  'order/removeAllItemRequest': 'remove-all-item-{checkoutType}',
  'delivery/pushMethodRequest': 'push-method-{areaId}-{type}',
};

/**
 * A single unit of work managed by the queue store.
 *
 * An element is created by `add()`, parked in `queue` (or `errorQueue`), and
 * only later executed by `runAction()` / `startErrorWorker()`. Its captured
 * `resolve`/`reject` bridge the action's result back to the original caller
 * that awaited `add()`.
 */
type QueueElement<TPayload = unknown, TResult = unknown> = {
  /**
   * The deferred work itself: a function that receives `payload` and returns a
   * Promise (typically a wrapped API request). It is NOT invoked by `add()` —
   * the worker calls it when the element becomes eligible to run.
   */
  action (payload: TPayload): Promise<TResult>;
  /**
   * Identifier of the action, e.g. `'order/addToCartRequest'`. Used to look up
   * elements in the queue and to derive loading IDs via
   * `LOADING_IDS_BY_ACTION_NAME`.
   */
  actionName: string;
  /** Argument passed to `action(payload)` when the element runs. */
  payload: TPayload;
  /**
   * Whether this element may run in parallel with elements already in the
   * working state, instead of waiting for the queue to drain.
   */
  canRunSimultaneous?: boolean;
  /**
   * Whether this element, while running, permits a second action to start
   * alongside it. A working element with this `false` blocks others from
   * starting simultaneously.
   */
  canStartSecondAction?: boolean;
  /** Unique id (`uuid4()`) tracking the element across the queue and working set. */
  uuid: string;
  /**
   * Resolves the Promise returned by `add()` — with the action's result, or with
   * `QES_SUPERSEDED_LOOPBLOCK` when the element is force-unblocked (428 loop).
   * Declared as a method so heterogeneous elements store into `QueueElement[]`.
   */
  resolve (value: TResult | typeof QES_SUPERSEDED_LOOPBLOCK): void;
  /** Rejects the Promise returned by `add()` if `action` throws. */
  reject (reason?: unknown): void;
};

export const useQueueStore = defineStore('queue', () => {
  const nuxtApp = useNuxtApp();

  const queue = ref<QueueElement[]>([]);
  const errorQueue = ref<QueueElement[]>([]);
  const workingQueueElements = ref<string[]>([]);
  const workingErrorQueueElements = ref<string[]>([]);
  const isErrorWorking = ref(false);
  const isBasketDependentWorking = ref(false);

  const hasActionInQueue = computed(() => (actionName: string) => {
    return queue.value.some(queueElement => queueElement.actionName === actionName);
  });
  const hasActionsInQueue = computed(() => (actionNameArr: string[]) => {
    return queue.value.some(queueElement => actionNameArr.includes(queueElement.actionName));
  });
  const hasFetchCheckoutInQueue = computed(() => (actionName: string, type: unknown) => {
    return queue.value.some(queueElement => {
      if (queueElement.actionName === actionName) {
        if (typeof queueElement.payload === 'object' && queueElement.payload && ('type' in queueElement.payload)) {
          return queueElement.payload.type === type;
        }
      }
      return false;
    });
  });

  function _setIsErrorWorking (newValue: boolean) {
    isErrorWorking.value = newValue;
  }

  function addActionToQueue (data: QueueElement) {
    queue.value.push(data);
  }

  function addActionToErrorQueue (data: QueueElement) {
    errorQueue.value.push(data);
  }

  function _removeActionFromQueue (paramUuid: QueueElement['uuid']) {
    const index = queue.value.findIndex(element => element.uuid === paramUuid);
    if (index > -1) {
      queue.value.splice(index, 1);
    }
  }

  function _removeFirstActionFromErrorQueue () {
    errorQueue.value.shift();
  }

  function _addQueueElementToWorkingState (queueElement: QueueElement) {
    workingQueueElements.value.push(queueElement.uuid);
  }

  function _removeQueueElementToWorkingState (queueElementUuid: QueueElement['uuid']) {
    const index = workingQueueElements.value.findIndex(uuid => uuid === queueElementUuid);
    if (index > -1) {
      workingQueueElements.value.splice(index, 1);
    }
  }

  function removeAllElementFromQueue (forceAllRemove: boolean) {
    if (forceAllRemove) {
      queue.value = [];
    } else {
      // remove loading state first
      queue.value.forEach((element) => {
        const { actionName } = element;
        let loadingId = LOADING_IDS_BY_ACTION_NAME[actionName];
        if (loadingId) {
          if (typeof element.payload === 'object' && element.payload) {
            const payload = element.payload as Record<string, unknown>;
            let data: Record<string, unknown> = {};
            if (typeof payload.data === 'object' && payload.data) {
              data = payload.data as Record<string, unknown>;
            }
            loadingId = (loadingId
              .replaceAll('{data.objectId}', String(data?.objectId))
              .replaceAll('{data.stockType}', String(data?.stockType))
              .replaceAll('{id}', String(payload?.id))
              .replaceAll('{objectId}', String(payload?.objectId))
              .replaceAll('{uuid}', String(payload?.uuid))
              .replaceAll('{checkoutType}', String(payload?.checkoutType))
              .replaceAll('{areaId}', String(payload?.areaId))
              .replaceAll('{type}', String(payload?.type))
            );
          }
          useUserInterfaceStore().endLoading({ id: loadingId });
        }
      });

      queue.value = queue.value.filter(element => workingQueueElements.value.includes(element.uuid));
    }
  }

  function add<TPayload, TResult> ({ action, actionName, payload, canRunSimultaneous, canStartSecondAction }: Omit<QueueElement<TPayload, TResult>, 'uuid' | 'resolve' | 'reject'>) {
    const uuid = uuid4();
    return new Promise<TResult | typeof QES_SUPERSEDED_LOOPBLOCK>((resolve, reject) => {
      const data = { action, actionName, payload, canStartSecondAction, canRunSimultaneous, uuid, resolve, reject };

      if ((typeof payload === 'object' && payload) && ('resolveOption' in payload) && payload?.resolveOption) {
        addActionToErrorQueue(data);
      } else {
        addActionToQueue(data);
      }
    });
  }

  function startWorker () {
    const { $logger } = nuxtApp;
    const firstQueueElement = queue.value[0];
    if (firstQueueElement) {
      if (!workingQueueElements.value.length) {
        runAction(firstQueueElement);
      } else {
        const canRunSimultaneousElements = queue.value.find(element => element.canRunSimultaneous && !workingQueueElements.value.includes(element.uuid));

        if (canRunSimultaneousElements) {
          const cannotStartSecondAction = queue.value.some(element => !element.canStartSecondAction && workingQueueElements.value.includes(element.uuid));
          if (!cannotStartSecondAction) {
            runAction(canRunSimultaneousElements);
          }
        }
      }
    } else if (workingQueueElements.value.length) {
      $logger.error('Houston, There is a problem, there aren\'t any acton in the queue, but there are some element in the workingQueueElements array. UUIDs: ', workingQueueElements.value);
    }
  }

  function startErrorWorker () {
    const firstErrorQueueElement = errorQueue.value[0];
    if (!isErrorWorking.value && firstErrorQueueElement) {
      isErrorWorking.value = true;

      const { action, payload, resolve, reject } = firstErrorQueueElement;

      action(payload)
        .then((r) => {
          _removeFirstActionFromErrorQueue();
          isErrorWorking.value = false;

          resolve(r);
        })
        .catch((e) => {
          _removeFirstActionFromErrorQueue();
          isErrorWorking.value = false;

          reject(e);
        });
    }
  }

  function runAction (element: QueueElement) {
    const { action, payload, resolve, reject, uuid } = element;
    _addQueueElementToWorkingState(element);

    action(payload)
      .then((r) => {
        _removeQueueElementToWorkingState(uuid);
        _removeActionFromQueue(uuid);
        resolve(r);
      })
      .catch((e: unknown) => {
        _removeQueueElementToWorkingState(uuid);
        _removeActionFromQueue(uuid);
        reject(e);
      });
  }

  function checkPreviousErrorAndResolveIfNeeded ({
    previous,
    error,
    actionName,
    errorScope,
    httpCode,
  }: {
    previous: number;
    error: unknown;
    actionName: string;
    errorScope: 'coldParcel';
    httpCode: number;
  }) {
    const errorObj = isApiError(error) ? error : undefined;
    if (previous === httpCode && errorObj?.response?.status === previous && errorObj?.response?.data?.scope === errorScope) {
      //black magic logic here to handle 428 error loop
      //if we got 428 error after a resolved 428 error, we got an infinite waiting loop, so we resolve the first 428 and after try it again
      const runningQueueUpdateCartItemRequest = queue.value.find(queueItem => queueItem.actionName === actionName);
      if (runningQueueUpdateCartItemRequest) {
        _removeQueueElementToWorkingState(runningQueueUpdateCartItemRequest.uuid);
        _removeActionFromQueue(runningQueueUpdateCartItemRequest.uuid);
        runningQueueUpdateCartItemRequest.resolve(QES_SUPERSEDED_LOOPBLOCK);
      }
      const runningErrorQueueUpdateCartItemRequest = errorQueue.value.find(queueItem => queueItem.actionName === actionName);
      if (runningErrorQueueUpdateCartItemRequest) {
        _removeFirstActionFromErrorQueue();
        _setIsErrorWorking(false);
        runningErrorQueueUpdateCartItemRequest.resolve(QES_SUPERSEDED_LOOPBLOCK);
      }
    }
  }

  return {
    queue,
    errorQueue,
    workingQueueElements,
    workingErrorQueueElements,
    isErrorWorking,
    isBasketDependentWorking,
    hasActionInQueue,
    hasActionsInQueue,
    hasFetchCheckoutInQueue,
    addActionToQueue,
    addActionToErrorQueue,
    removeAllElementFromQueue,
    add,
    startWorker,
    startErrorWorker,
    runAction,
    checkPreviousErrorAndResolveIfNeeded,
  };
});
